import moment from "moment";

const Statistics = {
  /**
   * Calculate the compounded annual growth rate.
   * @param {object} returnSeries an object with dates as keys and returns as values
   * @returns {number}
   */
  cagr: function (returnSeries) {
    if (!returnSeries) {
      return undefined;
    }

    const dates = Object.keys(returnSeries);
    const nDays = dates.length;
    const cumReturn = dates.reduce(
      (r, date) => (r *= 1 + returnSeries[date]),
      1.0
    );
    return nDays === 0 ? undefined : Math.pow(cumReturn, 252 / nDays) - 1;
  },
  /**
   * Calculate the mean of the elements in the series.
   * @param {Object} returnSeries
   * @returns {number}
   */
  mean: function (returnSeries) {
    const returns = Object.keys(returnSeries).map((date) => returnSeries[date]);
    const n = returns.length;
    const sum = returns.reduce((sum, elem) => (sum += elem), 0.0);
    return n !== 0.0 ? sum / n : undefined;
  },
  /**
   * Calculate the populational standard deviation of the elements in the series.
   * @param {object} returnSeries an object with dates as keys and returns as values
   * @returns {number}
   */
  stdevp: function (returnSeries) {
    const returns = Object.keys(returnSeries).map((date) => returnSeries[date]);
    const mu = Statistics.mean(returns);
    const n = returns.length;
    const sum = returns.reduce(
      (sum, elem) => (sum += Math.pow(elem - mu, 2)),
      0.0
    );
    return n !== 0.0 ? Math.sqrt(sum / n) : undefined;
  },
  /**
   * Calculate the sample standard deviation of the elements in the series.
   * @param {object} returnSeries an object with dates as keys and returns as values
   * @returns {number}
   */
  stdevs: function (returnSeries) {
    const returns = Object.keys(returnSeries).map((date) => returnSeries[date]);
    const mu = Statistics.mean(returns);
    const n = returns.length - 1;
    const sum = returns.reduce(
      (sum, elem) => (sum += Math.pow(elem - mu, 2)),
      0.0
    );

    return n - 1 !== 0.0 ? Math.sqrt(sum / n) : undefined;
  },
  volp: function (returnSeries) {
    return Statistics.stdevp(returnSeries) * Math.sqrt(252);
  },
  vols: function (returnSeries) {
    return Statistics.stdevs(returnSeries) * Math.sqrt(252);
  },
  quotasSeries: function (returnSeries, base = 1) {
    const quotas = {};
    const dates = Object.keys(returnSeries);
    let prevQuota = base;
    for (let i = 0; i < dates.length; i++) {
      const date = dates[i];
      const quota = prevQuota * (1 + returnSeries[date]);
      quotas[date] = quota;
      prevQuota = quota;
    }

    return quotas;
  },
  sharpe: function (returnSeries, benchmarkSeries) {
    if (!returnSeries || !benchmarkSeries) {
      return undefined;
    }

    const dates = Object.keys(returnSeries);
    const benchmarkDates = Object.keys(benchmarkSeries);

    // Check if benchmark and returnSeries dates are different. If it is,
    // raise an error.
    const difference = dates.filter((d) => !benchmarkDates.includes(d));
    if (difference.length > 0) {
      throw new Error(
        "The length of benchmark and portfolio returnSeries must be the same to calculate sharpe"
      );
    }

    // Build alpha series
    const alphaSeries = {};
    dates.forEach((date) => {
      alphaSeries[date] = returnSeries[date] - benchmarkSeries[date];
    });

    // Calculate return cagr, benchmark cagr and alpha vol
    const cagrReturns = Statistics.cagr(returnSeries);
    const cagrBenchmark = Statistics.cagr(benchmarkSeries);
    const volAlpha = Statistics.volp(alphaSeries);

    // Return sharpe
    return volAlpha !== 0.0
      ? (cagrReturns - cagrBenchmark) / volAlpha
      : undefined;
  },
  maxDrawdown: function (returnSeries) {
    const quotas = Statistics.quotasSeries(returnSeries);
    const dates = Object.keys(quotas);

    // Calcualte drawdowns
    let max = quotas[dates[0]];
    const drawdowns = [];

    for (let i = 1; i < dates.length; i++) {
      const date = dates[i];
      const quota = quotas[date];
      max = quota > max ? quota : max;
      drawdowns.push(quota / max - 1);
    }

    return Math.min(...drawdowns);
  },
  returnOnPeriod: function (returnSeries, period) {
    const quotasSeries = Statistics.quotasSeries(returnSeries);
    const dates = Object.keys(quotasSeries).map(
      (d) => new Date(d + "T00:00:00")
    );

    // Find dates such as the date is less than the period startDate
    const lowerDates = dates.filter((d) => d < period.startDate);

    if (lowerDates.length === 0) return undefined;

    // Set start and end dates
    const startDate = lowerDates[lowerDates.length - 1];
    const endDate = period.endDate;

    // Filter dates
    const filteredDates = dates
      .filter((d) => d >= startDate && d <= endDate)
      .map((d) => moment(d).format("YYYY-MM-DD"));

    // Calculate return
    if (filteredDates.length > 0) {
      const firstQuota = quotasSeries[filteredDates[0]];
      const lastQuota = quotasSeries[filteredDates[filteredDates.length - 1]];
      return firstQuota !== 0.0 ? lastQuota / firstQuota - 1 : undefined;
    }

    return 0.0;
  },
  returnMTD: function (returnSeries) {
    const dates = Object.keys(returnSeries);

    if (dates.length === 0) {
      return undefined;
    }

    // Create period
    const endDate = new Date(dates[dates.length - 1] + "T00:00:00");
    const period = {
      startDate: new Date(endDate.getFullYear(), endDate.getMonth(), 1),
      endDate: endDate,
    };

    return Statistics.returnOnPeriod(returnSeries, period);
  },
  returnYTD: function (returnSeries) {
    const dates = Object.keys(returnSeries);

    if (dates.length === 0) {
      return undefined;
    }

    // Create period
    const endDate = new Date(dates[dates.length - 1] + "T00:00:00");
    const period = {
      startDate: new Date(endDate.getFullYear(), 0, 1),
      endDate: endDate,
    };

    return Statistics.returnOnPeriod(returnSeries, period);
  },
  return3M: function (returnSeries) {
    const dates = Object.keys(returnSeries);

    if (dates.length === 0) {
      return undefined;
    }

    // Create period
    const endDate = new Date(dates[dates.length - 1] + "T00:00:00");
    const period = {
      startDate: new Date(endDate).setMonth(endDate.getMonth() - 3),
      endDate: endDate,
    };

    return Statistics.returnOnPeriod(returnSeries, period);
  },
  return6M: function (returnSeries) {
    const dates = Object.keys(returnSeries);

    if (dates.length === 0) {
      return undefined;
    }

    // Create period
    const endDate = new Date(dates[dates.length - 1] + "T00:00:00");
    const period = {
      startDate: new Date(endDate).setMonth(endDate.getMonth() - 6),
      endDate: endDate,
    };

    return Statistics.returnOnPeriod(returnSeries, period);
  },
  return12M: function (returnSeries) {
    const dates = Object.keys(returnSeries);

    if (dates.length === 0) {
      return undefined;
    }

    // Create period
    const endDate = new Date(dates[dates.length - 1] + "T00:00:00");
    const period = {
      startDate: new Date(endDate).setMonth(endDate.getMonth() - 12),
      endDate: endDate,
    };

    return Statistics.returnOnPeriod(returnSeries, period);
  },
  returnSI: function (returnSeries) {
    const dates = Object.keys(returnSeries);

    if (dates.length === 0) {
      return undefined;
    }

    // Create period
    const endDate = new Date(dates[dates.length - 1] + "T00:00:00");
    const startDate = new Date(dates[1] + "T00:00:00");
    const period = {
      startDate: startDate,
      endDate: endDate,
    };

    return Statistics.returnOnPeriod(returnSeries, period);
  },
};

export default Statistics;
